import {
  RXDB_INVOICE_COLLECTION_NAME,
  type Invoice as RxInvoice,
} from "$global-providers/rxdb-provider/collections/invoice";
import { Invoice } from "$routes/_lib/billing-v2/invoice";
import type { PayerSummary } from "$routes/patient/[:patientId]/_lib/invoices/components/InvoiceCard/types";
import { useEffect, useMemo } from "react";
import { useRxData } from "rxdb-hooks";
import { invoiceRecordFromDocument } from "./invoice-record-from-document";

/**
 * Reads the patient's invoices from rxdb and maps them onto the v2 list
 * row shape (`{ invoiceId, invoiceSummary }`).
 *
 * `invoiceId` is the rxdb primary key (`doc.id`, which the api-billing
 * syncable mapper sets to `mongoose.uuid`) — *not* the legacy mongo
 * `_id`. That makes the id consistent across v2 list → v2 detail, with
 * no bridge field needed on the legacy GQL `Invoice` type.
 *
 * Facility / organisation names and creator name aren't stored on the
 * rxdb invoice doc; ids are surfaced as-is and v2 consumers join from
 * the patient/facility rxdb collections at the presentation layer when
 * a name is actually needed (most rows don't display them).
 */
const hasStaleDeletedFlag = (meta: RxInvoice["meta"] | undefined): boolean =>
  Boolean((meta as { deleted?: boolean } | undefined)?.deleted);

export const mapInvoiceSummary = (record: RxInvoice) => {
  const invoice = Invoice.fromRecord(record).toRecord();
  return {
    invoiceId: invoice.id,
    invoiceSummary: {
      created: invoice.meta.createdAt,
      invoiceNumber: invoice.invoiceNumber,
      invoiceStatus: invoice.invoiceStatus,
      patientType: String(invoice.patientType),
      creator: "",
      payers: invoice.payers.map<PayerSummary>((payer) => ({
        type: String(payer.type),
        name: payer.name,
        balance: { paid: payer.balance.paid, due: payer.balance.due },
      })),
      balance: {
        total: invoice.balance.total,
        paid: invoice.balance.paid,
        due: invoice.balance.due,
        currency: invoice.balance.currency,
      },
      facility: { id: invoice.facilityId, name: "" },
      organisation: { id: "", name: "" },
    },
  };
};

export function useFetchPatientInvoicesRxdb(patientId: string) {
  const { result, isFetching } = useRxData<RxInvoice>(
    RXDB_INVOICE_COLLECTION_NAME,
    (collection) => collection.find({ selector: { patientId } }),
  );

  // Older builds flagged deletion as `meta.deleted` doc data, which never
  // replicated as a delete — finish those as real removes.
  useEffect(() => {
    for (const doc of result ?? []) {
      if (hasStaleDeletedFlag(doc.meta)) {
        doc
          .getLatest()
          .remove()
          .catch(() => {
            // already removed elsewhere
          });
      }
    }
  }, [result]);

  const invoices = useMemo(() => {
    return (result ?? [])
      .filter((doc) => !hasStaleDeletedFlag(doc.meta))
      .map((doc) => mapInvoiceSummary(invoiceRecordFromDocument(doc)))
      .sort(
        (a, b) =>
          new Date(b.invoiceSummary.created).valueOf() -
          new Date(a.invoiceSummary.created).valueOf(),
      );
  }, [result]);

  return { invoices, loading: isFetching, error: null as Error | null };
}
